import "./BaseSmallModalFormComponent.css";
import { BaseCreateComponent } from "../BaseCreateComponent";
import { BaseItemResponse } from "../../response/BaseItemResponse";
import { BasePayload } from "../../services/api/payload/BasePayload";
import { ServiceSaveBase } from "../../services/base/ServiceSaveBase";
import { errorMessageComponent } from "../error/ErrorMessageComponent";

export abstract class BaseSmallModalFormComponent<
  TItem extends BaseItemResponse,
  TPayload extends BasePayload,
  TSaveService extends ServiceSaveBase<TItem, TPayload>,
> extends BaseCreateComponent<HTMLDivElement> {
  constructor(protected readonly saveService: TSaveService) {
    super();
  }

  protected getTagName(): string { return "div"; }
  protected getElementId(): string { return "sxSmallModalOverlay"; }
  protected getCssClassList(): string[] { return ["sx-dlg-overlay"]; }

  // ── Abstractos ────────────────────────────────────────────

  protected abstract get title(): string;
  protected abstract renderBody(): string;
  protected abstract buildPayload(): TPayload | Promise<TPayload>;

  // ── Hooks opcionales ──────────────────────────────────────

  protected get saveText(): string { return "Guardar"; }
  protected get cancelText(): string { return "Cancelar"; }
  protected get saveInitiallyDisabled(): boolean { return true; }

  protected onSaved(_item: TItem): void { this.dispose(); }
  protected onAfterRender(_el: HTMLDivElement): void {}

  // ── API pública ───────────────────────────────────────────

  protected saveBtn!: HTMLButtonElement;
  protected cancelBtn!: HTMLButtonElement;

  protected setSaveDisabled(disabled: boolean): void {
    this.saveBtn.disabled = disabled;
  }

  // ── Lifecycle ─────────────────────────────────────────────

  protected override afterInit(el: HTMLDivElement): void {
    el.innerHTML = `
      <div class="sx-dlg-card">
        <div class="sx-dlg-header">
          <span class="sx-dlg-title">${this.title}</span>
          <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm sx-dlg-close-btn" type="button" aria-label="Cerrar">
            <i class="ph ph-x"></i>
          </button>
        </div>
        <div class="sx-dlg-body">${this.renderBody()}</div>
        <div class="sx-dlg-footer">
          <button class="sx-btn sx-btn-ghost sx-dlg-cancel-btn" type="button">${this.cancelText}</button>
          <button class="sx-btn sx-btn-primary sx-dlg-save-btn" type="button" ${this.saveInitiallyDisabled ? "disabled" : ""}>${this.saveText}</button>
        </div>
      </div>
    `;

    this.cancelBtn = el.querySelector<HTMLButtonElement>(".sx-dlg-cancel-btn")!;
    this.saveBtn   = el.querySelector<HTMLButtonElement>(".sx-dlg-save-btn")!;

    this.cancelBtn.onclick = () => this.dispose();
    el.querySelector<HTMLButtonElement>(".sx-dlg-close-btn")!.onclick = () => this.dispose();
    this.saveBtn.onclick = () => this._handleSave();

    el.addEventListener("click", (e) => { if (e.target === el) this.dispose(); });

    this.onAfterRender(el);
  }

  public override dispose(): void {
    const el = this.getElement();
    el.classList.add("exit");
    setTimeout(() => super.dispose(), 180); // esperar a que termine la animación CSS antes de quitar el DOM
  }

  // ── Guardado ──────────────────────────────────────────────

  private async _handleSave(): Promise<void> {
    this.saveBtn.disabled = true;
    try {
      const payload = await this.buildPayload();
      const item = await this.saveService.save(payload);
      this.onSaved(item);
    } catch (error) {
      errorMessageComponent.addError(
        "Error al guardar",
        error instanceof Error ? error.message : "Error desconocido.",
      );
      this.saveBtn.disabled = false;
    }
  }
}
